// DynamicPage.jsx
import { useLocation } from 'react-router-dom';
// Assuming styles are in DynamicPage.module.css
// import styles from './DynamicPage.module.css';

// For demonstration, I'll use inline styles or assume styles are handled.
// To make it complete, define styles as an object here for the response.

const styles = {
  app: { minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f0f0' },
  container: { maxWidth: '800px', padding: '20px' },
  section: { marginBottom: '40px' },
  headline: { fontSize: '2em', marginBottom: '10px' },
  subheadline: { fontSize: '1.5em', marginBottom: '10px' },
  content: { fontSize: '1em', marginBottom: '10px' },
  items: { listStyleType: 'disc', paddingLeft: '20px' },
  item: { marginBottom: '5px' },
  cta: { padding: '10px 20px', background: '#007bff', color: 'white', border: 'none', cursor: 'pointer' },
  form: { display: 'flex', flexDirection: 'column' },
  formField: { marginBottom: '10px' },
  formLabel: { marginBottom: '5px' },
  formInput: { padding: '8px', fontSize: '1em' },
  formTextarea: { padding: '8px', fontSize: '1em', height: '100px' },
  contentExtra: { fontStyle: 'italic' },
};

// content.json - Combine all provided page data with standardized paths matching navbar
const data = {
  "routes": [
    {
      "path": "/ai-lead/crm",
      "label": "AI Lead Agent – CRM Integration",
      "pageKey": "aiLeadCrm"
    },
    {
      "path": "/ai-lead/nurturing",
      "label": "AI Lead Agent – Lead Nurturing",
      "pageKey": "aiLeadNurturing"
    },
    {
      "path": "/ai-recruiter/outreach",
      "label": "AI Recruiter – Candidate Outreach",
      "pageKey": "aiRecruiterOutreach"
    },
    {
      "path": "/ai-lead/generation",
      "label": "AI Lead Agent – Lead Generation",
      "pageKey": "aiLeadGeneration"
    },
    {
      "path": "/ai-recruiter/resume",
      "label": "AI Recruiter – Resume Screening",
      "pageKey": "aiRecruiterResume"
    },
    {
      "path": "/ai-recruiter/sourcing",
      "label": "AI Recruiter – Talent Sourcing",
      "pageKey": "aiRecruiterSourcing"
    },
    {
      "path": "/ai-sales/lead-qualification",
      "label": "AI Sales Agent – Lead Qualification",
      "pageKey": "aiSalesLeadQualification"
    },
    {
      "path": "/ai-booking/appointment",
      "label": "AI Booking Agent – Appointment & Reservation Management",
      "pageKey": "aiBookingAppointment"
    },
    {
      "path": "/ai-support/live-chat",
      "label": "AI Customer Support – Live Chat Support",
      "pageKey": "aiSupportLiveChat"
    },
    {
      "path": "/ai-support/ticket-resolution",
      "label": "AI Customer Support – Ticket Resolution",
      "pageKey": "aiSupportTicketResolution"
    },
    {
      "path": "/ai-support/feedback",
      "label": "AI Customer Support – Customer Feedback",
      "pageKey": "aiSupportFeedback"
    },
    {
      "path": "/ai-tech/troubleshooting",
      "label": "AI Tech Support – Technical Troubleshooting",
      "pageKey": "aiTechTroubleshooting"
    },
    {
      "path": "/ai-tech/software",
      "label": "AI Tech Support – Software Assistance",
      "pageKey": "aiTechSoftware"
    },
    {
      "path": "/ai-tech/hardware",
      "label": "AI Tech Support – Hardware Support",
      "pageKey": "aiTechHardware"
    },
    {
      "path": "/ai-sales/pitch",
      "label": "AI Sales Agent – Sales Pitch Automation",
      "pageKey": "aiSalesPitch"
    },
    {
      "path": "/ai-sales/follow-up",
      "label": "AI Sales Agent – Follow-Up Management",
      "pageKey": "aiSalesFollowUp"
    },
    {
      "path": "/ai-interviewer/screening",
      "label": "AI Interviewer – Candidate Screening",
      "pageKey": "aiInterviewerScreening"
    },
    {
      "path": "/ai-interviewer/scheduling",
      "label": "AI Interviewer – Interview Scheduling",
      "pageKey": "aiInterviewerScheduling"
    },
    {
      "path": "/ai-interviewer/assessment",
      "label": "AI Interviewer – Assessment Analysis",
      "pageKey": "aiInterviewerAssessment"
    },
    {
      "path": "/ai-booking/reservation",
      "label": "AI Booking Agent – Reservation Management",
      "pageKey": "aiBookingReservation"
    },
    {
      "path": "/ai-booking/calendar",
      "label": "AI Booking Agent – Calendar Integration",
      "pageKey": "aiBookingCalendar"
    },
    {
      "path": "/contact",
      "label": "Contact Us",
      "pageKey": "contactUs"
    },
    {
      "path": "/why-us",
      "label": "Why Toxbox",
      "pageKey": "whyToxbox"
    },
    {
      "path": "/news",
      "label": "News & Media",
      "pageKey": "news"
    },
    {
      "path": "/careers",
      "label": "Careers",
      "pageKey": "careers"
    }
  ],
  "pages": {
    "aiLeadCrm": {
      "sections": [
        {
          "headline": "AI Lead Agent – Seamless CRM Integration with ToxBox AI",
          "subheadline": "Connect your CRM, automate lead follow-ups, and boost conversions with AI-powered workflows.",
          "cta": "Integrate Your CRM Now"
        },
        {
          "headline": "Stop Losing Leads Due to Disconnected Systems",
          "content": "Managing leads across multiple platforms is inefficient and error-prone. ToxBox AI integrates directly with your CRM, ensuring all leads are automatically captured, segmented, and nurtured without manual intervention.",
          "items": [
            "60% of leads are lost due to slow or inconsistent follow-ups",
            "AI-driven CRM integration improves lead conversion rates by up to 50%",
            "Reduce manual lead management effort by 80%"
          ]
        },
        {
          "headline": "Connect, Automate, and Nurture Leads",
          "items": [
            "Step 1: CRM Integration – Sync ToxBox AI with HubSpot, Salesforce, Zoho, Pipedrive, and more. Capture leads from website forms, ad campaigns, and social channels.",
            "Step 2: Automated Lead Segmentation – AI segments leads based on engagement, interest, and buying intent. Prioritize high-value prospects.",
            "Step 3: Personalized Follow-Ups – Automated emails, SMS, and chat outreach directly from CRM. AI personalizes messaging based on lead data and behavior.",
            "Step 4: Engagement Analytics – Monitor lead activity, response rates, and pipeline progression. AI recommends optimal nurturing actions.",
            "Step 5: Continuous Nurturing – AI keeps leads engaged until they’re ready to buy. Re-engage dormant leads automatically."
          ]
        },
        {
          "headline": "AI-Powered CRM Features",
          "items": [
            "One-Click CRM Integration – Sync leads from any platform",
            "Automated Follow-Up Sequences – Set once, run continuously",
            "Multi-Channel Engagement – Emails, SMS, LinkedIn, Chat",
            "Lead Scoring & Prioritization – Focus on high-potential prospects",
            "Behavior-Based Triggers – AI adapts messaging based on engagement",
            "Analytics & Reporting – Monitor ROI and pipeline growth"
          ]
        },
        {
          "headline": "Why Integrate ToxBox AI with Your CRM",
          "items": [
            "Save Time – Reduce manual entry and repetitive tasks. Let AI handle lead nurturing sequences.",
            "Increase Conversions – Engage leads consistently with personalized messages. Prioritize the most promising prospects.",
            "Better Insights – Track performance across campaigns and channels. Optimize follow-ups based on AI recommendations.",
            "Seamless Workflow – Unified lead management across CRM and website. No more disconnected systems or missed leads."
          ]
        },
        {
          "headline": "Works with Leading CRM Tools",
          "items": [
            "HubSpot – Sync leads and automate campaigns",
            "Salesforce – AI-driven follow-ups and pipeline tracking",
            "Zoho CRM – Segment, nurture, and convert leads automatically",
            "Pipedrive, Freshsales, and more – Flexible integration for any business"
          ],
          "content": "“After integrating ToxBox AI with Salesforce, Company X improved follow-up efficiency by 70% in just 30 days.”",
          "cta": "Connect Your CRM"
        },
        {
          "headline": "Businesses Trust ToxBox AI CRM Integration",
          "items": [
            "“Integrating ToxBox AI with HubSpot automated our lead nurturing and boosted conversions.” – Marketing Manager, TechCo",
            "“Salesforce integration with AI lead sequences saved us countless hours.” – Sales Director, FinTechCorp"
          ]
        },
        {
          "headline": "Frequently Asked Questions",
          "items": [
            "Which CRMs does ToxBox AI support? – HubSpot, Salesforce, Zoho, Pipedrive, Freshsales, and custom CRMs via API.",
            "Can AI automate follow-ups within CRM? – Yes, ToxBox AI creates personalized sequences directly within your CRM.",
            "Is lead data secure? – Yes, enterprise-grade security and GDPR-compliant."
          ]
        },
        {
          "headline": "Flexible CRM Integration Plans",
          "cta": "Get Started with CRM Integration"
        },
        {
          "headline": "Supercharge Your CRM with ToxBox AI",
          "content": "Integrate your CRM, automate lead follow-ups, and boost conversions with AI-powered lead nurturing. Start transforming your sales pipeline today.",
          "cta": "Integrate ToxBox AI Now"
        }
      ]
    },
    "aiLeadNurturing": {
      "metaDescription": "ToxBox AI’s Lead Nurturing tool automates follow-ups, personalizes outreach, and boosts conversions. Transform your lead management today.",
      "sections": [
        {
          "headline": "AI Lead Agent – Smarter Lead Nurturing with ToxBox AI",
          "subheadline": "Automate personalized follow-ups, nurture leads at scale, and increase conversions with AI-driven workflows.",
          "cta": "Start Nurturing Leads Smarter"
        },
        {
          "headline": "Stop Losing Leads to Manual Follow-Ups",
          "content": "Most businesses struggle to stay on top of their leads. Slow or inconsistent follow-ups lead to missed opportunities and wasted marketing spend. ToxBox AI’s Lead Nurturing automates follow-ups, personalizes engagement, and ensures no lead is left behind.",
          "items": [
            "70% of leads never convert due to delayed follow-ups",
            "Personalized follow-ups can increase conversions by 50–60%",
            "AI-powered sequences reduce manual effort by up to 80%"
          ]
        },
        {
          "headline": "Intelligent Lead Nurturing, Step by Step",
          "items": [
            "Step 1: Lead Capture & Segmentation – Sync leads from website, CRM, or ad campaigns. AI segments leads based on engagement and intent.",
            "Step 2: Automated Personalized Outreach – Emails, SMS, chat messages, and LinkedIn outreach. Dynamic personalization using lead data.",
            "Step 3: Smart Follow-Up Sequences – AI schedules follow-ups at optimal times. Adjusts messaging based on lead behavior.",
            "Step 4: Engagement Analytics & Optimization – Track opens, clicks, replies, and conversion likelihood. AI suggests improvements for higher engagement.",
            "Step 5: Continuous Nurturing – Ongoing engagement with dormant leads. Re-engage cold leads automatically."
          ],
          "content": "“Company X improved conversion by 3x using ToxBox AI’s Lead Nurturing, with automated sequences handling 90% of follow-ups.”"
        },
        {
          "headline": "AI Features That Drive Lead Engagement",
          "items": [
            "Dynamic Lead Segmentation – Prioritize high-value leads",
            "Multi-Channel Nurturing – Email, SMS, LinkedIn, Chat",
            "Automated Follow-Up Workflows – Set once, run continuously",
            "Behavioral Tracking – Engage leads based on actions",
            "CRM & Website Integration – Connect ToxBox AI to your ecosystem",
            "Analytics & Reporting – Monitor ROI and lead progression"
          ],
          "content": "“Nurture leads automatically while focusing on closing deals.”"
        },
        {
          "headline": "Why ToxBox AI Lead Nurturing Works",
          "items": [
            "Time Savings – Automate repetitive follow-ups. Reduce manual effort by 80%.",
            "Higher Conversions – Engage leads consistently. Personalized messaging increases response rate.",
            "Better Insights – AI recommends best times and channels. Analytics help optimize campaigns.",
            "Customer Retention – Keep leads warm until they’re ready to buy. Reduce lead drop-off rates."
          ]
        },
        {
          "headline": "Works Seamlessly with Your Tools",
          "items": [
            "Supported Platforms: CRM (HubSpot, Salesforce, Zoho), Website forms, Email platforms, Chatbots",
            "Auto-sync leads, track engagement, and trigger automated sequences",
            "Custom workflows for your sales and marketing teams"
          ],
          "content": "“Company Y integrated ToxBox AI with HubSpot and increased follow-up efficiency by 60% within a month.”",
          "cta": "Integrate ToxBox AI Now"
        },
        {
          "headline": "Trusted by Businesses Worldwide",
          "items": [
            "“ToxBox AI completely changed how we nurture leads. Our engagement rate skyrocketed!” – Marketing Manager, FinTechCo",
            "“Automated follow-ups saved us countless hours and improved conversions.” – Sales Director, TechSolutions"
          ]
        },
        {
          "headline": "Frequently Asked Questions",
          "items": [
            "Can ToxBox AI send multi-channel follow-ups? – Yes, it automates emails, SMS, LinkedIn messages, and website chat interactions.",
            "Is personalization automated? – Absolutely. AI tailors messages dynamically using lead data.",
            "Can I integrate it with my website? – Yes, ToxBox AI integrates with forms, CRM, and other sales tools.",
            "Is my lead data secure? – Yes, enterprise-grade security and GDPR-compliant."
          ]
        },
        {
          "headline": "Flexible Plans for Every Business",
          "cta": "Start Nurturing Leads"
        },
        {
          "headline": "Nurture Leads Smarter with ToxBox AI",
          "content": "Stop losing leads due to delayed follow-ups. Let ToxBox AI automate, personalize, and optimize your lead nurturing process. Close deals faster, save time, and boost revenue.",
          "cta": "Get Started with ToxBox AI Today"
        }
      ]
    },
    "aiRecruiterOutreach": {
      "metaDescription": "Engage top candidates instantly with ToxBox AI. Automate outreach campaigns, send hyper-personalized messages, track engagement, and hire smarter.",
      "sections": [
        {
          "headline": "AI Recruiter – Supercharge Candidate Outreach with ToxBox AI",
          "subheadline": "Automate personalized outreach, engage top talent faster, and close hires efficiently with AI-driven recruitment campaigns.",
          "cta": "Start Smart Candidate Outreach Today"
        },
        {
          "headline": "Struggling to Engage Top Candidates? Let AI Do the Heavy Lifting.",
          "content": "Recruiters often spend 50–70% of their time on repetitive outreach. ToxBox AI automates these tasks while keeping communication personal, timely, and engaging.",
          "items": [
            "70% of recruiters say outreach is their most time-consuming task",
            "Personalized AI messaging boosts response rates 3–5x",
            "Automated follow-ups increase candidate engagement by up to 80%"
          ],
          "contentExtra": "“Before ToxBox AI, our recruitment team spent 15 hours per week on outreach. After automation, we saved 12 hours and doubled our response rate.” – HR Manager, TechNova"
        },
        {
          "headline": "How ToxBox AI Engages Candidates – Step by Step",
          "items": [
            "Import Candidate Data – Upload from LinkedIn, ATS, or CSV. Smart segmentation by skills, experience, location, and engagement history.",
            "AI-Powered Personalization – AI crafts hyper-personalized messages and adjusts tone for seniority, role type, or industry.",
            "Automated Campaign Scheduling – Send messages at optimal times via Email, LinkedIn, SMS, WhatsApp.",
            "Real-Time Engagement Tracking – Analytics for opens, clicks, replies, and response time. Dashboard highlights candidates needing follow-ups.",
            "Automated Follow-Ups – AI sends reminders to non-responders and customizes frequency, message type, and tone."
          ],
          "contentExtra": "“Company X used ToxBox AI and increased candidate response rates from 12% to 48% in 30 days, cutting time-to-hire by 25%.”"
        },
        {
          "headline": "Powerful Features to Maximize Candidate Engagement",
          "items": [
            "Hyper-Personalized AI Messages",
            "Bulk Outreach with Precision",
            "Multi-Channel Engagement",
            "Automated Follow-Ups",
            "Seamless ATS Integration",
            "Analytics & Reporting"
          ],
          "contentExtra": "“Focus on hiring decisions, not repetitive outreach. Let AI handle the mundane tasks while you close top talent faster.”"
        },
        {
          "headline": "Transform Your Recruitment Process with AI",
          "items": [
            "Time-Saving Benefits – Automate repetitive outreach and follow-ups",
            "Improved Candidate Experience – AI personalization ensures each candidate feels valued",
            "Data-Driven Insights – Analytics show which campaigns and messages work best",
            "Faster Hiring Process – Reduced time-to-hire with automated engagement",
            "Cost Efficiency – Lower overheads and reduce reliance on staffing agencies"
          ],
          "contentExtra": [
            "Companies using AI recruitment report 40% faster time-to-hire",
            "Average response rate for AI-personalized messages: 42–55%",
            "Automated outreach reduces recruiter workload by up to 70%"
          ]
        },
        {
          "headline": "Seamless Integration with Your ATS & Platforms",
          "items": [
            "Auto-sync candidate lists",
            "Real-time updates for response tracking",
            "Multi-channel outreach automation"
          ],
          "contentExtra": "“Company Y integrated ToxBox AI with Greenhouse and automated outreach to 1,000+ candidates weekly, increasing hiring efficiency by 40%.”",
          "cta": "Integrate with Your ATS Now"
        },
        {
          "headline": "Trusted by Recruitment Teams Globally",
          "items": [
            "“ToxBox AI helped us engage candidates faster and more effectively than ever before.” – HR Lead, TechCorp",
            "“Our response rate jumped from 15% to 52% in the first month.” – Talent Acquisition Manager, FinServe"
          ]
        },
        {
          "headline": "Frequently Asked Questions",
          "items": [
            { "q": "Can AI send personalized messages?", "a": "Yes, every message is automatically personalized based on candidate data and job role." },
            { "q": "How do follow-ups work?", "a": "AI schedules automated reminders for non-responders and keeps engagement high without manual effort." },
            { "q": "Does it integrate with ATS and LinkedIn?", "a": "Absolutely. ToxBox AI integrates with all major ATS platforms and LinkedIn." },
            { "q": "Is candidate data secure?", "a": "Industry-standard encryption, GDPR, and privacy compliance ensure full security." },
            { "q": "Can I track campaign performance?", "a": "Yes, real-time analytics help optimize outreach strategy for maximum engagement." }
          ]
        },
        {
          "headline": "Flexible Plans for Every Recruitment Team",
          "cta": "View Pricing Plans"
        },
        {
          "headline": "Start Engaging Candidates Smarter with AI Today",
          "content": "Stop sending repetitive outreach manually. Let ToxBox AI automate candidate messaging, follow-ups, and engagement tracking. Hire top talent faster, increase response rates, and reduce time-to-hire.",
          "cta": "Get Started Now"
        }
      ]
    },
    "aiLeadGeneration": {
      "metaDescription": "Generate leads faster with AI Lead Agent. Automate prospecting, personalize outreach, track engagement, and grow your sales pipeline efficiently.",
      "sections": [
        {
          "headline": "AI Lead Agent – Supercharge Your Lead Generation",
          "subheadline": "Automate prospecting, generate high-quality leads, and boost conversions with AI-driven campaigns. Transform your sales pipeline today.",
          "cta": "Start Generating Leads Smarter"
        },
        {
          "headline": "Struggling to Find High-Quality Leads? AI Can Help.",
          "content": "Sales teams often waste hours on manual prospecting, cold outreach, and tracking leads that never respond. AI Lead Agent streamlines this process by automatically identifying prospects, personalizing outreach, and nurturing leads through the sales funnel.",
          "items": [
            "Sales reps spend up to 60% of their time on lead generation",
            "Personalized outreach increases response rates by 3x",
            "AI-assisted campaigns can boost qualified leads by up to 50%"
          ]
        },
        {
          "headline": "How AI Lead Agent Generates High-Quality Leads – Step by Step",
          "items": [
            "Prospect Identification – Import contacts from CRM, LinkedIn, or CSV. AI scans profiles, identifies buying signals, and scores leads.",
            "AI-Powered Personalization – Craft custom messages for each prospect. Adjust tone for industry, role, and company size.",
            "Automated Campaign Scheduling – Send messages via email, LinkedIn, SMS, or WhatsApp. Schedule for peak engagement times.",
            "Engagement Tracking & Analytics – Monitor opens, clicks, replies, and response times. Dashboard highlights hot leads ready for follow-up.",
            "Lead Nurturing Automation – AI automatically follows up with unresponsive leads. Customizable sequence and messaging style."
          ],
          "contentExtra": "Company Z increased lead conversion by 3x in 45 days using AI Lead Agent, cutting prospecting time in half."
        },
        {
          "headline": "Powerful Features for Maximum Lead Conversion",
          "items": [
            "Smart Lead Scoring: Prioritize leads with the highest potential",
            "Personalized Outreach: Tailored messages for each prospect",
            "Multi-Channel Engagement: Email, LinkedIn, SMS, WhatsApp",
            "Automated Follow-Ups: Nurture leads automatically",
            "Seamless CRM Integration: Salesforce, HubSpot, Zoho, Pipedrive",
            "Analytics & Reporting: Track campaign performance and ROI"
          ],
          "contentExtra": "Spend less time searching for leads and more time closing deals."
        },
        {
          "headline": "Transform Your Sales Process with AI",
          "items": [
            "Time-Saving Benefits – Automate lead research and outreach. Save 50–70% of manual prospecting time.",
            "Improved Lead Quality – Focus on high-intent prospects. Reduce wasted effort on low-quality leads.",
            "Data-Driven Insights – AI identifies top-performing messaging strategies. Optimize campaigns for better ROI.",
            "Faster Sales Cycle – Automated nurturing accelerates deals. Engage more leads faster.",
            "Cost Efficiency – Reduce dependency on outsourced lead generation. Streamline internal sales operations."
          ]
        },
        {
          "headline": "Seamless Integration with Your Sales Stack",
          "items": [
            "Auto-sync lead lists",
            "Real-time updates for engagement tracking",
            "Multi-channel automation"
          ],
          "contentExtra": "Sales team at Company A integrated AI Lead Agent with HubSpot and increased qualified leads by 40% in just one month.",
          "cta": "Integrate with Your CRM Now"
        },
        {
          "headline": "Trusted by Sales Teams Worldwide",
          "items": [
            "AI Lead Agent transformed our prospecting process. Response rates doubled within weeks. – Sales Director, TechSolutions",
            "We saw a 3x increase in qualified leads without hiring additional reps. – VP of Sales, FinTechCo"
          ]
        },
        {
          "headline": "Frequently Asked Questions",
          "items": [
            { "q": "Can AI send personalized messages?", "a": "Yes, every message is tailored automatically to each lead’s profile." },
            { "q": "How do follow-ups work?", "a": "AI sends automated reminders to unresponsive prospects with customizable sequences." },
            { "q": "Does it integrate with CRM and LinkedIn?", "a": "Absolutely, it supports major CRM platforms and LinkedIn integration for seamless workflow." },
            { "q": "Is lead data secure?", "a": "Yes, with enterprise-grade encryption and full compliance with data privacy regulations." }
          ]
        },
        {
          "headline": "Flexible Plans for Every Sales Team"
        },
        {
          "headline": "Start Generating Leads Smarter with AI Today",
          "content": "Stop wasting hours on manual prospecting. Let AI Lead Agent automate your outreach, nurture leads, and grow your pipeline. Close more deals faster with smarter lead generation.",
          "cta": "Get Started Now"
        }
      ]
    },
    "aiRecruiterResume": {
      "title": "AI Recruiter – Smart Resume Screening with ToxBox AI",
      "meta_description": "Automate resume screening with ToxBox AI. Rank candidates accurately, save hours of manual work, and hire the best talent efficiently.",
      "seo_keywords": ["AI resume screening", "automated resume analysis", "AI candidate ranking", "smart recruitment software"],
      "sections": [
        {
          "type": "hero",
          "headline": "AI Recruiter – Smart Resume Screening with ToxBox AI",
          "subheadline": "Instantly screen, rank, and shortlist the best candidates with AI-powered resume analysis. Hire smarter, faster, and with confidence.",
          "cta": { "text": "Start Smart Resume Screening", "link": "#get-started" }
        },
        {
          "type": "text",
          "headline": "Revolutionize Recruitment with ToxBox AI",
          "content": "Recruitment can be tedious. Sorting through hundreds of resumes manually is slow, prone to human error, and exhausting. ToxBox AI uses advanced algorithms to screen resumes instantly, extract relevant skills, and highlight top talent. Recruiters can now focus on what matters most – interviewing and hiring the right candidates."
        },
        {
          "type": "steps",
          "headline": "How AI Makes Resume Screening Smarter",
          "content": "With ToxBox AI, every resume is evaluated consistently, helping recruiters make data-driven hiring decisions without bias or delays.",
          "items": [
            "Upload resumes in bulk (PDF, DOCX, TXT).",
            "AI parses resumes to extract skills, experience, education, and certifications.",
            "Candidates are scored and ranked based on your job requirements.",
            "Apply filters for role-specific needs, such as skills, experience, or location.",
            "Automatically shortlist the best candidates for interviews."
          ]
        },
        {
          "type": "features",
          "headline": "Powerful Features Designed for Modern Recruitment",
          "content": "ToxBox AI brings speed, accuracy, and intelligence to resume screening. Recruiters no longer spend days sifting through resumes – they get actionable insights instantly.",
          "items": [
            "AI-powered parsing & ranking of resumes",
            "Skill, experience, and role-based candidate scoring",
            "Bulk upload & instant analysis of hundreds of resumes",
            "Customizable filters for precise shortlisting",
            "Seamless integration with ATS & HR platforms",
            "Real-time analytics and insights for smarter hiring decisions"
          ]
        },
        {
          "type": "benefits",
          "headline": "Why Recruiters Love AI Resume Screening",
          "items": [
            "Cut manual resume review time by up to 80%",
            "Identify top talent faster with AI scoring",
            "Reduce human bias in candidate evaluation",
            "Automate shortlisting and candidate filtering",
            "Gain insights from analytics for smarter hiring",
            "Improve overall recruitment efficiency and ROI"
          ]
        },
        {
          "type": "use_cases",
          "headline": "Who Can Benefit from AI Resume Screening?",
          "content": "ToxBox AI is perfect for:",
          "items": [
            "HR Teams & Recruiters: Streamline hiring pipelines and reduce workload.",
            "Staffing Agencies: Quickly match candidates with client requirements.",
            "Large Enterprises: Handle high-volume applications efficiently.",
            "Startups & SMEs: Hire top talent without hiring a large HR team."
          ]
        },
        {
          "type": "integration",
          "headline": "Seamless Integration with Your HR Tools",
          "content": "ToxBox AI works with popular ATS and HR platforms like Workday, BambooHR, Greenhouse, SAP SuccessFactors, and more. Automate every step from parsing to candidate shortlisting, creating a frictionless recruitment workflow.",
          "cta": { "text": "Integrate with Your HR System", "link": "#integration" }
        },
        {
          "type": "testimonials",
          "headline": "Trusted by Recruiters Worldwide",
          "items": [
            "\"ToxBox AI cut our resume screening time by 70%! The candidate ranking is precise, and the integration with our ATS is seamless.\" – HR Manager, Global Tech Firm",
            "\"Using AI for shortlisting has helped us find the best talent faster, improving our hiring quality significantly.\" – Recruitment Lead, Fintech Startup"
          ]
        },
        {
          "type": "faq",
          "headline": "Frequently Asked Questions About AI Resume Screening",
          "items": [
            { "question": "Can AI parse all types of resumes?", "answer": "Yes, ToxBox AI supports PDF, DOCX, and TXT formats." },
            { "question": "How does AI rank candidates?", "answer": "Based on skills, experience, and role suitability." },
            { "question": "Does it integrate with ATS?", "answer": "Yes, ToxBox AI connects with most major HR platforms." },
            { "question": "Is candidate data secure?", "answer": "Absolutely. Data is encrypted and compliant with global standards." },
            { "question": "Can it reduce hiring bias?", "answer": "Yes, AI ensures consistent evaluation for every candidate." }
          ]
        },
        {
          "type": "pricing",
          "headline": "Flexible Plans for Every Hiring Need",
          "content": "Start with a free plan or scale with premium plans offering unlimited resume screening, advanced analytics, multi-channel integration, and enterprise-grade security. ToxBox AI grows with your recruitment needs.",
          "cta": { "text": "View Pricing Plans", "link": "#pricing" }
        },
        {
          "type": "resources",
          "headline": "Learn More About AI in Recruitment",
          "content": "Access guides, case studies, and blogs on how AI transforms hiring processes, reduces bias, and improves efficiency."
        },
        {
          "type": "final_cta",
          "headline": "Start Screening Resumes Smarter with AI",
          "content": "Stop spending hours reviewing resumes manually. Let ToxBox AI automate resume parsing, ranking, and shortlisting, so you can hire the best candidates faster.",
          "cta": { "text": "Get Started Now", "link": "#get-started" }
        }
      ]
    },
    "aiRecruiterSourcing": {
      "sections": [
        {
          "type": "hero",
          "content": {
            "title": "AI Recruiter – Smart Talent Sourcing with ToxBox AI",
            "subtitle": "Find the right candidates instantly. Automate talent sourcing, screening, and outreach with AI-powered recruitment software.",
            "meta_description": "Automate candidate sourcing and recruitment with ToxBox AI. Find top talent faster with AI-powered candidate screening and outreach."
          },
          "cta": { "text": "Start Sourcing Top Talent" }
        },
        {
          "type": "introduction",
          "content": {
            "title": "Why Choose ToxBox AI for Talent Sourcing?",
            "text": "ToxBox AI revolutionizes recruitment by streamlining candidate sourcing, reducing manual effort, and identifying top talent quickly. Save time, improve hiring quality, and scale recruitment efficiently."
          }
        },
        {
          "type": "how_it_works",
          "content": {
            "title": "How ToxBox AI Streamlines Recruitment",
            "steps": [
              "Define job requirements and candidate profiles",
              "AI scans resumes, LinkedIn, and job boards",
              "Rank candidates by skills, experience, and fit",
              "Automated outreach to shortlisted candidates",
              "Track responses and schedule interviews"
            ],
            "text": "Using AI, candidate sourcing and screening become effortless, ensuring faster, smarter, and more accurate recruitment."
          }
        },
        {
          "type": "features",
          "content": {
            "title": "Powerful Features of ToxBox AI Recruiter",
            "items": [
              "AI-powered candidate sourcing and screening",
              "Resume parsing and ranking",
              "Multi-platform search (LinkedIn, job portals, databases)",
              "Automated candidate outreach via email & messaging",
              "ATS & HRIS integration"
            ],
            "text": "Boost recruitment efficiency, reduce hiring time, and ensure you never miss top talent with ToxBox AI."
          }
        },
        {
          "type": "benefits",
          "content": {
            "title": "Why HR Teams & Businesses Love ToxBox AI",
            "items": [
              "Save time on manual candidate searches",
              "Identify the best-fit talent quickly",
              "Automate candidate outreach and follow-ups",
              "Improve hiring efficiency and reduce costs",
              "Gain actionable insights through AI analytics"
            ]
          }
        },
        {
          "type": "integration",
          "content": {
            "title": "Seamless ATS & HR System Integration",
            "text": "ToxBox AI integrates with HR platforms like Workday, BambooHR, Greenhouse, and more. Automate sourcing, ranking, and outreach for a fully streamlined hiring process."
          },
          "cta": { "text": "Integrate with Your HR System" }
        },
        {
          "type": "testimonials",
          "content": {
            "title": "Trusted by Recruiters Worldwide",
            "text": "See how ToxBox AI helps companies source top talent faster, reduce hiring bottlenecks, and improve candidate engagement. Join hundreds of satisfied HR professionals leveraging AI to optimize recruitment."
          }
        },
        {
          "type": "faq",
          "content": {
            "title": "Frequently Asked Questions About AI Talent Sourcing",
            "items": [
              { "question": "Can AI screen resumes automatically?", "answer": "Yes, instantly and accurately." },
              { "question": "Does it integrate with existing HR software?", "answer": "Absolutely." },
              { "question": "How does AI rank candidates?", "answer": "Based on skills, experience, and relevance." },
              { "question": "Is candidate data secure?", "answer": "Yes, with encrypted storage and privacy compliance." }
            ]
          }
        },
        {
          "type": "pricing",
          "content": {
            "title": "Flexible Pricing for Every Recruitment Need",
            "text": "Choose a plan tailored to your hiring volume. Start free or upgrade for unlimited AI sourcing, multi-channel outreach, and advanced analytics. ToxBox AI scales with your recruitment strategy."
          },
          "cta": { "text": "View Pricing Plans" }
        },
        {
          "type": "final_cta",
          "content": {
            "title": "Start Sourcing Top Talent Smarter with AI",
            "text": "Stop wasting time on manual recruitment. Let ToxBox AI automate talent sourcing, screening, and outreach so you can hire the best candidates efficiently."
          },
          "cta": { "text": "Get Started Now" }
        }
      ]
    },
    "aiSalesLeadQualification": {
      "sections": [
        {
          "headline": "AI Sales Agent – Qualify Leads Instantly",
          "subheadline": "Transform your sales process with our AI-powered lead qualification system. Identify high-quality leads in seconds, prioritize prospects, and focus on closing deals faster.",
          "cta": "Get Started with AI Lead Qualification",
          "items": []
        },
        {
          "headline": "Why Choose Our AI Sales Agent?",
          "content": "Our AI sales agent revolutionizes the way businesses handle leads. No more wasted time on unqualified prospects. Experience automated lead scoring, intelligent prioritization, and actionable insights to supercharge your sales pipeline.",
          "items": []
        },
        {
          "headline": "How Our AI Lead Qualification System Works",
          "items": [
            "Import your leads from CRM, spreadsheets, or marketing platforms.",
            "AI analyzes lead data in real-time.",
            "Automatically scores and prioritizes leads based on intent, engagement, and conversion probability.",
            "Receive actionable insights and follow-up recommendations.",
            "Focus on high-value leads while the AI manages the rest."
          ],
          "content": "Using advanced AI algorithms, your sales team can focus on prospects that are most likely to convert, reducing wasted effort and accelerating revenue growth."
        },
        {
          "headline": "Powerful Features of Our AI Sales Agent",
          "items": [
            "Automated Lead Scoring & Prioritization",
            "CRM Integration (Salesforce, HubSpot, Zoho, etc.)",
            "Real-Time Lead Insights & Recommendations",
            "Predictive Analytics for Conversion Probability",
            "Multi-channel Lead Tracking (Email, Calls, Chat, Web Forms)"
          ],
          "content": "Maximize your sales efficiency and close more deals with an AI-powered lead qualification system trusted by sales teams worldwide."
        },
        {
          "headline": "Qualify Leads in Seconds",
          "content": "Our AI Sales Agent instantly analyzes incoming leads, scores them, and provides your team with actionable insights. Spend less time on low-value leads and more time closing deals. Seamless integration with your workflow ensures real-time results.",
          "cta": "Start Qualifying Leads Now",
          "items": []
        },
        {
          "headline": "Why Businesses Rely on Our AI Lead Qualification Agent",
          "items": [
            "Reduce time spent on unqualified leads.",
            "Increase conversion rates with data-driven insights.",
            "Automate lead prioritization and follow-ups.",
            "Improve sales team productivity and revenue performance.",
            "Gain visibility into pipeline health and lead trends."
          ]
        },
        {
          "headline": "Seamless CRM Integration for Smarter Sales",
          "content": "Our AI Sales Agent integrates with Salesforce, HubSpot, Zoho, and other popular CRMs. Automatically sync lead scores, track engagement, and receive predictive analytics directly in your CRM dashboard. Optimize your sales workflow with minimal effort.",
          "cta": "Integrate Your CRM Today",
          "items": []
        },
        {
          "headline": "Trusted by Sales Teams Worldwide",
          "content": "See how businesses and sales professionals leverage our AI Sales Agent to prioritize leads, accelerate sales cycles, and boost conversion rates. Join hundreds of satisfied teams already achieving smarter sales outcomes.",
          "items": []
        },
        {
          "headline": "Frequently Asked Questions About AI Sales Agent",
          "items": [
            "Can I customize lead scoring criteria? – Yes, fully customizable to fit your sales process.",
            "Is my lead data secure? – Absolutely. We use industry-standard encryption and secure storage.",
            "Can it handle multiple lead sources? – Yes, our AI consolidates and analyzes leads from any platform.",
            "Does it provide follow-up recommendations? – Yes, AI suggests the best actions for high-priority leads."
          ]
        },
        {
          "headline": "Simple, Transparent Pricing",
          "content": "Choose the plan that fits your business needs. Start for free or upgrade for advanced AI features such as unlimited lead scoring, CRM automation, predictive analytics, and priority support. Scale your sales process effortlessly with ToxBox AI.",
          "cta": "View Pricing Plans",
          "items": []
        },
        {
          "headline": "Start Qualifying Leads Smarter Today",
          "content": "Stop wasting time on low-value leads. Let our AI Sales Agent identify and prioritize the prospects most likely to convert. Boost sales efficiency, close deals faster, and scale your business with AI-powered lead qualification.",
          "cta": "Get Started Now",
          "items": []
        }
      ]
    },
    "aiBookingAppointment": {
      "sections": [
        {
          "headline": "AI Appointment Booking Agent – Schedule Appointments Instantly",
          "subheadline": "Streamline your scheduling process with ToxBox AI, the intelligent appointment and reservation management solution. Book meetings, consultations, classes, or reservations in seconds—without the hassle of endless calls, emails, or manual scheduling. Let AI handle confirmations, reminders, and calendar integration automatically.",
          "cta": "Book Your Appointment Instantly",
          "form": null,
          "items": []
        },
        {
          "headline": "Why Choose ToxBox AI Booking Agent?",
          "subheadline": "",
          "content": "ToxBox AI revolutionizes the way businesses, consultants, and service providers manage their appointments and reservations. No more double bookings, missed slots, or tedious manual scheduling. Our AI-powered system automates the entire booking workflow, saving you time and improving productivity. Whether you’re a small business, healthcare provider, tutor, or event organizer, ToxBox AI keeps your appointments organized, reliable, and hassle-free.",
          "cta": "",
          "form": null,
          "items": []
        },
        {
          "headline": "How ToxBox AI Appointment System Works",
          "subheadline": "",
          "content": "Select Your Service or Reservation Type: Choose the type of appointment, consultation, or service you need.\n\nPick a Date and Time: Use the intuitive interface to select your preferred slot.\n\nInstant AI Confirmation: Our AI instantly confirms your booking and sends notifications.\n\nReceive Automated Reminders: Get timely reminders via email and SMS, ensuring you never miss an appointment.\n\nWith AI technology powering every step, scheduling becomes effortless. Our system ensures efficient time management for professionals, teams, and customers, reducing missed appointments and increasing overall satisfaction. ToxBox AI works 24/7 to handle bookings so you can focus on what matters most—your business.",
          "cta": "",
          "form": null,
          "items": []
        },
        {
          "headline": "Powerful Features of ToxBox AI Booking Agent",
          "subheadline": "",
          "content": "ToxBox AI ensures your scheduling is efficient, reliable, and professional. Businesses around the globe trust our AI booking agent to reduce manual work, minimize errors, and improve client engagement. From small teams to large organizations, our platform adapts to your needs.",
          "cta": "",
          "form": null,
          "items": [
            "24/7 Online Booking: Clients can schedule anytime, anywhere, with no limitations.",
            "Real-Time Calendar Integration: Seamless sync with Google Calendar, Outlook, iCal, and more.",
            "Automated Email & SMS Notifications: Keep clients informed with instant updates.",
            "Easy Rescheduling & Cancellation: Flexible options for your customers.",
            "Smart Analytics & Reporting: Track appointments, no-shows, and client activity."
          ]
        },
        {
          "headline": "Reserve Your Appointment or Spot in Seconds",
          "subheadline": "",
          "content": "With ToxBox AI, booking appointments or reserving spots is simple and instantaneous. Select your service, pick a convenient date and time, and let our AI handle confirmations, notifications, and calendar updates automatically. No more waiting for replies or dealing with scheduling conflicts—your appointments are instantly organized and secure.",
          "cta": "Reserve Your Spot Now",
          "form": null,
          "items": []
        },
        {
          "headline": "Why Professionals & Businesses Prefer ToxBox AI",
          "subheadline": "",
          "content": "",
          "cta": "",
          "form": null,
          "items": [
            "Save Time: Automate repetitive scheduling tasks.",
            "Reduce Errors: Avoid double bookings or missed appointments.",
            "Efficient Multi-Appointment Management: Handle multiple services or staff schedules effortlessly.",
            "Enhance Customer Experience: Provide clients with smooth, professional booking experiences.",
            "Boost Productivity: Focus on business growth while AI manages your calendar."
          ]
        },
        {
          "headline": "Smart Calendar Integration for Effortless Scheduling",
          "subheadline": "",
          "content": "ToxBox AI integrates with all major calendar platforms, including Google Calendar, Outlook, iCal, and more. Every appointment is synced automatically, preventing double bookings and missed meetings. Receive real-time notifications and reminders directly in your calendar. Our AI-powered system ensures smooth, organized scheduling, no matter your business size or service type.",
          "cta": "Sync Your Calendar Today",
          "form": null,
          "items": []
        },
        {
          "headline": "Trusted by Professionals and Businesses Worldwide",
          "subheadline": "",
          "content": "'ToxBox AI has transformed the way we schedule appointments. It’s fast, reliable, and our clients love the instant confirmations!' – Sarah J., Clinic Manager\n\n'Managing multiple services has never been easier. AI reminders and calendar sync keep us on track.' – John D., Business Owner\n\nJoin hundreds of satisfied users who rely on ToxBox AI to manage appointments efficiently.",
          "cta": "",
          "form": null,
          "items": []
        },
        {
          "headline": "Frequently Asked Questions About ToxBox AI Booking Agent",
          "subheadline": "",
          "content": "",
          "cta": "",
          "form": null,
          "items": [
            "Can I reschedule appointments? – Yes, instantly online through the AI portal.",
            "Is my data secure? – Absolutely. We use top-level encryption and secure storage.",
            "Can it handle multiple services or staff? – Yes, our AI supports complex scheduling needs.",
            "Does it work across time zones? – Yes, real-time adjustments ensure global scheduling accuracy."
          ]
        },
        {
          "headline": "Transparent, Affordable Plans for Every Business",
          "subheadline": "",
          "content": "ToxBox AI offers flexible plans to suit businesses of all sizes. Start for free to explore basic scheduling features or upgrade to premium plans for unlimited appointments, advanced calendar integrations, priority support, and comprehensive analytics. Our transparent pricing ensures you get the features you need without hidden costs.",
          "cta": "View Pricing Plans",
          "form": null,
          "items": []
        },
        {
          "headline": "Start Scheduling Smarter Today with ToxBox AI",
          "subheadline": "",
          "content": "Stop wasting hours on manual scheduling. Let ToxBox AI manage your appointments efficiently and professionally. Book now and experience effortless appointment management with AI-powered confirmations, reminders, and calendar integration. Transform the way you manage your time and grow your business.",
          "cta": "Book Your Appointment Now",
          "form": null,
          "items": []
        },
        {
          "headline": "Footer",
          "subheadline": "",
          "content": "",
          "cta": "",
          "form": null,
          "items": []
        }
      ]
    },
    "aiSupportLiveChat": {
      "metaDescription": "Enhance customer engagement with ToxBox AI Live Chat Support. Automate responses, resolve queries instantly, and improve satisfaction.",
      "sections": [
        {
          "headline": "AI Customer Support – Live Chat Support with ToxBox AI",
          "subheadline": "Provide real-time assistance, automate responses, and boost customer satisfaction with AI-powered live chat.",
          "cta": "Start Live Chat Support"
        },
        {
          "headline": "Why Use AI for Live Chat Support?",
          "content": "Manual chat support can be overwhelming. ToxBox AI handles multiple queries simultaneously, providing instant responses and escalating complex issues to human agents.",
          "items": [
            "Handle unlimited chats 24/7",
            "Personalized responses based on customer data",
            "Reduce response time by 80%"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Integrate with your website or app",
            "AI detects customer intent",
            "Automated replies and suggestions",
            "Seamless handoff to live agents",
            "Analytics for performance tracking"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Multi-language support",
            "Chat history and context retention",
            "Integration with CRM and knowledge bases",
            "Customizable chat widgets",
            "Sentiment analysis"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Improve customer satisfaction",
            "Reduce support costs",
            "Increase agent productivity",
            "Gain valuable insights"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“ToxBox AI revolutionized our customer support.” – Support Manager, EcomCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Does it integrate with existing chat tools?", "a": "Yes, seamlessly." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Implement Live Chat Now"
        }
      ]
    },
    "aiSupportTicketResolution": {
      "metaDescription": "Automate ticket resolution with ToxBox AI. Categorize, prioritize, and resolve support tickets efficiently.",
      "sections": [
        {
          "headline": "AI Customer Support – Ticket Resolution with ToxBox AI",
          "subheadline": "Streamline ticket management, automate resolutions, and improve response times with AI.",
          "cta": "Start Ticket Resolution"
        },
        {
          "headline": "Challenges in Ticket Management",
          "content": "Manual ticket handling leads to delays. ToxBox AI automates categorization and resolution for faster service.",
          "items": [
            "Auto-categorize tickets",
            "Suggest solutions from knowledge base",
            "Escalate when needed"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Receive and parse tickets",
            "AI analyzes and prioritizes",
            "Automated responses or resolutions",
            "Integration with email and CRM",
            "Reporting on resolution times"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "AI-powered prioritization",
            "Automated workflows",
            "Multi-channel ticket ingestion",
            "Analytics dashboard"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Faster resolution times",
            "Reduced agent workload",
            "Higher customer satisfaction"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Cut resolution time by 60%.” – IT Manager, TechFirm"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Can it handle complex tickets?", "a": "Yes, with human escalation." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Automate Tickets Now"
        }
      ]
    },
    "aiSupportFeedback": {
      "metaDescription": "Collect and analyze customer feedback with ToxBox AI. Gain insights to improve products and services.",
      "sections": [
        {
          "headline": "AI Customer Support – Customer Feedback with ToxBox AI",
          "subheadline": "Automate feedback collection, analyze sentiments, and derive actionable insights.",
          "cta": "Start Collecting Feedback"
        },
        {
          "headline": "Importance of Feedback",
          "content": "Understanding customer opinions is key. ToxBox AI automates collection and analysis for better decisions.",
          "items": [
            "Multi-channel feedback gathering",
            "Sentiment analysis",
            "Trend identification"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Send automated surveys",
            "Analyze responses with AI",
            "Generate reports",
            "Integrate with CRM",
            "Follow-up automation"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Customizable surveys",
            "Real-time analytics",
            "NPS and CSAT tracking",
            "Integration with support tools"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Improved product development",
            "Higher retention rates",
            "Data-driven improvements"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Valuable insights from feedback.” – Product Manager, SaaSCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Is data secure?", "a": "Yes, GDPR compliant." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Analyze Feedback Now"
        }
      ]
    },
    "aiTechTroubleshooting": {
      "metaDescription": "Resolve technical issues faster with ToxBox AI Technical Troubleshooting. Automate diagnostics and solutions.",
      "sections": [
        {
          "headline": "AI Tech Support – Technical Troubleshooting with ToxBox AI",
          "subheadline": "Automate issue detection, diagnostics, and resolutions for technical support.",
          "cta": "Start Troubleshooting"
        },
        {
          "headline": "Technical Support Challenges",
          "content": "Diagnosing issues manually is time-consuming. ToxBox AI provides instant troubleshooting guides.",
          "items": [
            "AI-driven diagnostics",
            "Step-by-step solutions",
            "Integration with monitoring tools"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "User describes issue",
            "AI analyzes symptoms",
            "Suggests fixes",
            "Escalates if needed",
            "Logs for future reference"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Knowledge base integration",
            "Multi-device support",
            "Predictive maintenance"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Faster issue resolution",
            "Reduced downtime",
            "Improved user experience"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Resolved issues 50% faster.” – Tech Support Lead, ITCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Supports which devices?", "a": "All major hardware and software." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Implement Now"
        }
      ]
    },
    "aiTechSoftware": {
      "metaDescription": "Provide software assistance with ToxBox AI. Automate bug fixes, updates, and user guidance.",
      "sections": [
        {
          "headline": "AI Tech Support – Software Assistance with ToxBox AI",
          "subheadline": "Guide users through software issues, automate fixes, and ensure smooth operation.",
          "cta": "Start Software Assistance"
        },
        {
          "headline": "Software Support Needs",
          "content": "Users often struggle with software. ToxBox AI offers instant help and automation.",
          "items": [
            "Bug detection and fixing",
            "Update management",
            "User tutorials"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Analyze software logs",
            "Provide guided fixes",
            "Automate patches",
            "Integrate with dev tools",
            "Track recurring issues"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "AI chat for assistance",
            "Automated scripting",
            "Compatibility checks"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Reduced support tickets",
            "Faster user onboarding",
            "Better software stability"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Streamlined software support.” – DevOps Manager, SoftCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Integrates with which software?", "a": "Most popular platforms." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Assist Now"
        }
      ]
    },
    "aiTechHardware": {
      "metaDescription": "Offer hardware support with ToxBox AI. Diagnose issues, guide repairs, and prevent failures.",
      "sections": [
        {
          "headline": "AI Tech Support – Hardware Support with ToxBox AI",
          "subheadline": "Automate hardware diagnostics, provide repair guides, and predict failures.",
          "cta": "Start Hardware Support"
        },
        {
          "headline": "Hardware Support Challenges",
          "content": "Hardware issues require quick diagnosis. ToxBox AI uses AI for efficient support.",
          "items": [
            "Sensor data analysis",
            "Repair instructions",
            "Preventive alerts"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Collect hardware data",
            "AI diagnoses problems",
            "Suggest repairs",
            "Integrate with IoT",
            "Report trends"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Remote diagnostics",
            "Parts recommendation",
            "Warranty tracking"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Minimized downtime",
            "Cost savings on repairs",
            "Extended hardware life"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Efficient hardware management.” – Hardware Engineer, ManuCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Supports which hardware?", "a": "Computers, servers, peripherals." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Support Now"
        }
      ]
    },
    "aiSalesPitch": {
      "metaDescription": "Automate sales pitches with ToxBox AI. Create personalized presentations and engage prospects effectively.",
      "sections": [
        {
          "headline": "AI Sales Agent – Sales Pitch Automation with ToxBox AI",
          "subheadline": "Generate customized sales pitches, automate delivery, and track engagement.",
          "cta": "Start Pitch Automation"
        },
        {
          "headline": "Why Automate Sales Pitches?",
          "content": "Consistent pitching is key to sales. ToxBox AI personalizes and automates for better results.",
          "items": [
            "Dynamic content generation",
            "Multi-channel delivery",
            "Performance analytics"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Input prospect data",
            "AI creates pitch",
            "Automate sending",
            "Track opens and responses",
            "Optimize based on data"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Personalization templates",
            "A/B testing",
            "CRM integration"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Higher engagement rates",
            "Time savings for sales teams",
            "Improved conversion"
          ]
        },
        {
          "headline": "Trusted by Sales Teams",
          "items": [
            "“Pitches that convert.” – Sales Rep, SellCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Customizable pitches?", "a": "Yes, fully." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Automate Pitches Now"
        }
      ]
    },
    "aiSalesFollowUp": {
      "metaDescription": "Manage sales follow-ups with ToxBox AI. Automate reminders, personalize messages, and close more deals.",
      "sections": [
        {
          "headline": "AI Sales Agent – Follow-Up Management with ToxBox AI",
          "subheadline": "Automate follow-up sequences, track interactions, and nurture prospects.",
          "cta": "Start Follow-Up Management"
        },
        {
          "headline": "Importance of Follow-Ups",
          "content": "Most sales require multiple touchpoints. ToxBox AI ensures timely, personalized follow-ups.",
          "items": [
            "Automated sequences",
            "Behavior-based triggers",
            "Multi-channel support"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Set follow-up rules",
            "AI sends messages",
            "Track responses",
            "Adjust strategies",
            "Integrate with sales tools"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Customizable templates",
            "Analytics on effectiveness",
            "Escalation options"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Increased close rates",
            "Efficient sales process",
            "Better prospect relationships"
          ]
        },
        {
          "headline": "Trusted by Sales Teams",
          "items": [
            "“Never miss a follow-up.” – Sales Manager, DealCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Integrates with email?", "a": "Yes, and more." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Manage Follow-Ups Now"
        }
      ]
    },
    "aiInterviewerScreening": {
      "metaDescription": "Screen candidates efficiently with ToxBox AI Interviewer. Automate initial assessments and shortlisting.",
      "sections": [
        {
          "headline": "AI Interviewer – Candidate Screening with ToxBox AI",
          "subheadline": "Automate screening questions, evaluate responses, and shortlist top candidates.",
          "cta": "Start Candidate Screening"
        },
        {
          "headline": "Screening Challenges",
          "content": "Initial screening is time-intensive. ToxBox AI handles it with automated interviews.",
          "items": [
            "Video or text-based screening",
            "AI evaluation",
            "Bias reduction"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Set screening criteria",
            "AI conducts interviews",
            "Analyze answers",
            "Score and rank",
            "Report to HR"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Custom questions",
            "Natural language processing",
            "Integration with ATS"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Faster hiring",
            "Better candidate matches",
            "Scalable screening"
          ]
        },
        {
          "headline": "Trusted by HR Teams",
          "items": [
            "“Efficient and fair screening.” – HR Director, HireCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Reduces bias?", "a": "Yes, through standardized evaluation." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Screen Now"
        }
      ]
    },
    "aiInterviewerScheduling": {
      "metaDescription": "Schedule interviews seamlessly with ToxBox AI. Automate calendar checks and reminders.",
      "sections": [
        {
          "headline": "AI Interviewer – Interview Scheduling with ToxBox AI",
          "subheadline": "Coordinate interview times, sync calendars, and send reminders automatically.",
          "cta": "Start Scheduling"
        },
        {
          "headline": "Scheduling Hassles",
          "content": "Coordinating schedules is tricky. ToxBox AI automates it for efficiency.",
          "items": [
            "Calendar integration",
            "Time zone handling",
            "Automated invitations"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Check availabilities",
            "Suggest times",
            "Book slots",
            "Send confirmations",
            "Handle rescheduling"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Multi-participant support",
            "Reminders via email/SMS",
            "Integration with video tools"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Time savings",
            "Reduced no-shows",
            "Smooth process"
          ]
        },
        {
          "headline": "Trusted by HR Teams",
          "items": [
            "“No more scheduling headaches.” – Recruiter, TalentCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Works with Google Calendar?", "a": "Yes, and others." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Schedule Now"
        }
      ]
    },
    "aiInterviewerAssessment": {
      "metaDescription": "Analyze interview assessments with ToxBox AI. Score responses and provide insights.",
      "sections": [
        {
          "headline": "AI Interviewer – Assessment Analysis with ToxBox AI",
          "subheadline": "Evaluate interview performance, score candidates, and generate reports.",
          "cta": "Start Assessment Analysis"
        },
        {
          "headline": "Assessment Needs",
          "content": "Objective assessment is crucial. ToxBox AI provides data-driven insights.",
          "items": [
            "Speech and text analysis",
            "Skill matching",
            "Feedback generation"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Record interviews",
            "AI analyzes content",
            "Score based on criteria",
            "Generate summaries",
            "Share reports"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Custom scoring models",
            "Sentiment detection",
            "Benchmarking"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Fair evaluations",
            "Faster decisions",
            "Improved hiring quality"
          ]
        },
        {
          "headline": "Trusted by HR Teams",
          "items": [
            "“Insightful assessments.” – Interviewer, AssessCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Handles video interviews?", "a": "Yes." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Analyze Now"
        }
      ]
    },
    "aiBookingReservation": {
      "metaDescription": "Manage reservations efficiently with ToxBox AI. Automate bookings, confirmations, and changes.",
      "sections": [
        {
          "headline": "AI Booking Agent – Reservation Management with ToxBox AI",
          "subheadline": "Handle reservations, updates, and cancellations with AI automation.",
          "cta": "Start Reservation Management"
        },
        {
          "headline": "Reservation Challenges",
          "content": "Manual reservations lead to errors. ToxBox AI streamlines the process.",
          "items": [
            "Real-time availability",
            "Automated confirmations",
            "Change management"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Check availability",
            "Book reservations",
            "Send notifications",
            "Handle modifications",
            "Integrate with systems"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Multi-user access",
            "Payment integration",
            "Reporting"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "Error reduction",
            "Better customer experience",
            "Efficient operations"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Seamless reservations.” – Manager, HotelCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Supports group bookings?", "a": "Yes." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Manage Reservations Now"
        }
      ]
    },
    "aiBookingCalendar": {
      "metaDescription": "Integrate calendars seamlessly with ToxBox AI. Sync bookings and avoid conflicts.",
      "sections": [
        {
          "headline": "AI Booking Agent – Calendar Integration with ToxBox AI",
          "subheadline": "Sync calendars, automate updates, and prevent double bookings.",
          "cta": "Start Calendar Integration"
        },
        {
          "headline": "Calendar Management",
          "content": "Keeping calendars in sync is essential. ToxBox AI handles it automatically.",
          "items": [
            "Multi-calendar support",
            "Real-time syncing",
            "Conflict resolution"
          ]
        },
        {
          "headline": "How It Works",
          "items": [
            "Connect calendars",
            "AI monitors changes",
            "Update bookings",
            "Send alerts",
            "Generate reports"
          ]
        },
        {
          "headline": "Features",
          "items": [
            "Google, Outlook integration",
            "Time zone adjustments",
            "Custom rules"
          ]
        },
        {
          "headline": "Benefits",
          "items": [
            "No overlaps",
            "Time efficiency",
            "Better organization"
          ]
        },
        {
          "headline": "Trusted by Businesses",
          "items": [
            "“Perfect sync.” – Scheduler, EventCo"
          ]
        },
        {
          "headline": "FAQ",
          "items": [
            { "q": "Secure integration?", "a": "Yes." }
          ]
        },
        {
          "headline": "Get Started",
          "cta": "Integrate Calendars Now"
        }
      ]
    },
    "contactUs": {
      "sections": [
        {
          "headline": "Get in Touch with Toxbox AI",
          "content": "Have questions about our enterprise AI platform, AI-powered chatbots, or intelligent virtual assistants? Reach out to our team for inquiries, demos, or partnership opportunities.",
          "cta": "Request a Demo"
        },
        {
          "headline": "Send Us a Message",
          "content": "Fill out our web form for quick responses. Provide your details, and our experts in conversational AI and workflow automation will get back to you promptly.",
          "form": {
            "fields": [
              { "label": "Name", "type": "text", "placeholder": "Your Name" },
              { "label": "Email", "type": "email", "placeholder": "Your Email" },
              { "label": "Company", "type": "text", "placeholder": "Your Company" },
              { "label": "Role", "type": "text", "placeholder": "Your Role" },
              { "label": "Message", "type": "textarea", "placeholder": "Your Message" }
            ],
            "cta": "Submit"
          }
        },
        {
          "headline": "Direct Contact Options",
          "content": "Prefer direct contact? Reach our team at:",
          "items": [
            "Email: info@toxbox.ai",
            "Phone: +1-800-TOXBOX"
          ]
        },
        {
          "headline": "Our Global Presence",
          "content": "Connect with Toxbox AI offices worldwide for enterprise inquiries and demos. Highlight regional expertise in AI solutions for fintech, healthcare, e-commerce, and education."
        },
        {
          "headline": "See Toxbox AI in Action",
          "content": "Schedule a personalized demo to explore automated customer support AI, digital transformation solutions, and workflow automation with AI.",
          "cta": "Book Your Demo"
        },
        {
          "headline": "Need Assistance?",
          "content": "Our support team is ready to help with technical queries, integration support, or AI adoption strategies for your organization."
        },
        {
          "headline": "Frequently Asked Questions",
          "content": "Answers to common inquiries about Toxbox AI platforms, conversational AI integration, and enterprise AI solutions."
        },
        {
          "headline": "Connect With Us Online",
          "content": "Follow Toxbox AI on LinkedIn, Twitter, and YouTube for updates, tips, and industry insights on AI-powered chatbots and digital transformation with AI."
        },
        {
          "headline": "Stay Updated with Toxbox AI",
          "content": "Subscribe to our newsletter to receive the latest AI news, product updates, and enterprise conversational AI insights.",
          "cta": "Subscribe Now"
        }
      ]
    },
    "whyToxbox": {
      "sections": [
        {
          "headline": "Why Toxbox AI – The Best Enterprise Conversational AI Platform",
          "subheadline": "Toxbox AI is an enterprise conversational AI platform designed to help businesses transform customer interactions with AI-powered chatbots, intelligent virtual assistants, and workflow automation AI. Built for digital transformation, Toxbox delivers omnichannel customer engagement across web, mobile apps, WhatsApp, social media, and voice channels.",
          "cta": "Request a Free Demo"
        },
        {
          "headline": "10+ Years of Delivering Enterprise AI Solutions",
          "items": [
            "Automate customer support with AI chatbots for reducing support costs",
            "Increase sales with AI chatbots for lead generation",
            "Drive workflow automation using machine learning",
            "Improve customer experience with AI-driven virtual assistants for enterprises"
          ]
        },
        {
          "headline": "The Toxbox Advantage",
          "items": [
            "Natural Language Understanding (NLU) engine – Advanced NLP for business automation that ensures human-like conversations.",
            "Seamless integrations – CRM, ERP, HRMS, ticketing systems, and more.",
            "Secure conversational AI for regulated industries – GDPR compliant, enterprise-grade data protection.",
            "Real-time AI sentiment analysis platform – Know exactly how your customers feel.",
            "Enterprise AI platform built for scale – Handles millions of queries with 99.9% uptime."
          ]
        },
        {
          "headline": "Engage Customers Anywhere, Anytime",
          "items": [
            "Web & in-app chatbots",
            "WhatsApp Business AI assistants",
            "Voice AI for call centers & IVR",
            "Instagram, Facebook Messenger, Telegram & social channels",
            "SMS & email automation with AI"
          ]
        },
        {
          "headline": "Data That Drives Digital Transformation",
          "items": [
            "Conversation funnel analysis",
            "Customer sentiment tracking",
            "CSAT & NPS performance dashboards",
            "AI-driven optimization recommendations",
            "Insights that fuel digital transformation with conversational AI"
          ]
        },
        {
          "headline": "Built for Scale, Security & Success",
          "items": [
            "Multi-region hosting & global scalability",
            "Enterprise-level compliance & encryption",
            "99.9% uptime SLA for mission-critical systems",
            "APIs for custom workflows and integrations",
            "Trusted as a top AI customer support software solution worldwide"
          ]
        },
        {
          "headline": "Conversational AI Tailored for Your Industry",
          "items": [
            "AI solutions for fintech automation – Secure, compliant customer interactions",
            "Healthcare virtual assistant AI – Appointment booking, patient engagement",
            "Conversational AI for e-commerce engagement – Personalized shopping experiences, cart recovery",
            "AI-powered chatbots in education – Admissions, enrollment, and tutoring",
            "Travel industry AI chatbot automation – Booking management & multilingual support"
          ]
        },
        {
          "headline": "Proven Impact of Enterprise Conversational AI",
          "items": [
            "Reduce customer response time with AI by up to 70%",
            "Boost conversion rates with AI chatbots for lead generation",
            "Cut operational costs by automating repetitive queries",
            "Transform customer experience automation across industries"
          ]
        },
        {
          "headline": "Ready to Redefine Customer Engagement with Toxbox AI?",
          "subheadline": "Whether you’re exploring Toxbox AI vs traditional chatbots, or comparing top AI customer support software, one thing is clear: Toxbox delivers enterprise-grade conversational AI that drives results.",
          "cta": "Request a Personalized Demo"
        }
      ]
    },
    "news": {
      "sections": [
        {
          "headline": "Toxbox AI News & Media",
          "content": "Stay updated with the latest news, press releases, and media coverage about Toxbox AI's advancements in conversational AI and enterprise solutions."
        },
        {
          "headline": "Recent Announcements",
          "items": [
            "Toxbox AI launches new NLP engine for enhanced chatbot interactions – October 2025",
            "Partnership with Global Fintech Corp to streamline banking operations – September 2025",
            "Toxbox AI named a leader in AI customer support software by Industry Reports – August 2025"
          ]
        },
        {
          "headline": "Media Inquiries",
          "content": "For press and media inquiries, contact our team at press@toxbox.ai.",
          "cta": "Contact Press Team"
        }
      ]
    },
    "careers": {
      "sections": [
        {
          "headline": "Careers",
          "content": "Join Toxbox AI – Shaping the Future of Enterprise AI - Become part of a team that’s revolutionizing conversational AI for enterprises, workflow automation, and intelligent virtual assistants. Your work will empower businesses across fintech, healthcare, e-commerce, education, and travel."
        },
        {
          "headline": "Open Positions",
          "items": [
            "AI Research Scientist – Remote",
            "Software Engineer (NLP) – San Francisco, CA",
            "Customer Success Manager – New York, NY",
            "Product Manager (AI Platforms) – London, UK"
          ]
        },
        {
          "headline": "Why Work at Toxbox AI?",
          "items": [
            "Work on cutting-edge AI technologies for global enterprises.",
            "Collaborate with a diverse, innovative team.",
            "Flexible work environment with remote opportunities.",
            "Competitive benefits and growth opportunities."
          ]
        },
        {
          "headline": "Apply Today",
          "content": "Explore career opportunities and apply to join our mission to transform customer experiences with AI.",
          "cta": "View Open Roles"
        }
      ]
    }
  }
};

function DynamicPage() {
  const location = useLocation();

  // Find the current route's pageKey based on the pathname
  const currentRoute = data.routes.find((route) => route.path === location.pathname);

  // If no route is found, display a "Page Not Found" message
  if (!currentRoute) {
    return (
      <div style={styles.app}>
        <div style={styles.container}>
          <section style={styles.section}>
            <h2 style={styles.headline}>Page Not Found</h2>
            <p style={styles.content}>
              Sorry, the page you are looking for does not exist. Please check the URL or return to the homepage.
            </p>
            <a href="/" style={styles.cta}>Go to Homepage</a>
          </section>
        </div>
      </div>
    );
  }

  const pageKey = currentRoute.pageKey;
  const pageData = data.pages[pageKey];

  return (
    <div style={styles.app}>
      <div style={styles.container}>
        {pageData.sections.map((section, index) => {
          const headline = section.headline || section.content?.title || section.content?.headline;
          const subheadline = section.subheadline || section.content?.subtitle || section.content?.subheadline;
          const content = typeof section.content === 'string' ? section.content : section.content?.text || section.content?.content;
          const items = section.items || section.content?.items || section.content?.steps;
          const cta = section.cta;
          const contentExtra = section.contentExtra || section.content?.text;  // Fallback for some structures

          return (
            <section key={index} style={styles.section}>
              {headline && <h2 style={styles.headline}>{headline}</h2>}
              {subheadline && <h3 style={styles.subheadline}>{subheadline}</h3>}
              {content && <p style={styles.content} dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }} />}
              {items && (
                <ul style={styles.items}>
                  {items.map((item, idx) => (
                    <li key={idx} style={styles.item}>
                      {typeof item === 'string' ? item : (
                        <>
                          <strong>{item.q || item.question}</strong> – {item.a || item.answer}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {contentExtra && (
                typeof contentExtra === 'string' ? (
                  <p style={styles.contentExtra} dangerouslySetInnerHTML={{ __html: contentExtra.replace(/\n/g, '<br />') }} />
                ) : Array.isArray(contentExtra) ? (
                  <ul style={styles.items}>
                    {contentExtra.map((extra, idx) => (
                      <li key={idx} style={styles.item}>{extra}</li>
                    ))}
                  </ul>
                ) : null
              )}
              {cta && (
                typeof cta === 'string' ? (
                  <button style={styles.cta}>{cta}</button>
                ) : cta?.text && cta?.link ? (
                  <a href={cta.link} style={styles.cta}>{cta.text}</a>
                ) : null
              )}
              {section.form && (
                <form style={styles.form}>
                  {section.form.fields.map((field, idx) => (
                    <div key={idx} style={styles.formField}>
                      <label style={styles.formLabel}>{field.label}</label>
                      {field.type === 'textarea' ? (
                        <textarea style={styles.formTextarea} placeholder={field.placeholder} />
                      ) : (
                        <input type={field.type} style={styles.formInput} placeholder={field.placeholder} />
                      )}
                    </div>
                  ))}
                  <button type="submit" style={styles.cta}>{section.form.cta}</button>
                </form>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default DynamicPage;